import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Phone, ArrowRight, X, User, LogOut } from "lucide-react";
import api from "@/lib/api";
import { getMemberToken, clearMember } from "@/lib/member";
import { useSiteConfig, DEFAULT_NAV } from "@/lib/siteConfig";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

function NavLinkItem({ item, className, onClick }) {
  const to = item.to || "/";
  const external = /^https?:\/\//.test(to) || to.startsWith("/api");
  if (external) return <a href={to} onClick={onClick} className={className}>{item.label}</a>;
  return <Link to={to} onClick={onClick} className={className}>{item.label}</Link>;
}

export function Logo({ light = false }) {
  const [branding, setBranding] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cz_branding") || "null"); } catch { return null; }
  });

  useEffect(() => {
    api.get("/public/branding").then(({ data }) => {
      setBranding(data);
      localStorage.setItem("cz_branding", JSON.stringify(data));
    }).catch(() => {});
  }, []);

  if (branding?.logo_url) {
    return (
      <Link to="/" aria-label={`${branding.brand_name || "CoWorkZen"} home`} className="inline-flex items-center" data-testid="logo">
        <img src={branding.logo_url} alt={branding.brand_name || "CoWorkZen"} className="h-11 w-auto max-w-[220px] object-contain" />
      </Link>
    );
  }

  return (
    <Link to="/" aria-label="CoWorkZen home"
      className="font-display text-[2rem] font-extrabold tracking-tight leading-none select-none whitespace-nowrap"
      data-testid="logo">
      <span className={light ? "text-white" : "text-[#16161a]"}>CoW</span>
      <span className="text-primary underline decoration-[3px] underline-offset-[4px]">o</span>
      <span className="text-primary">rkZen</span>
    </Link>
  );
}

export default function Header({ phone = "+91 9953010006" }) {
  const [open, setOpen] = useState(false);
  const [member, setMember] = useState(null);
  const navigate = useNavigate();
  const cfg = useSiteConfig();
  const nav = cfg.nav_links?.length ? cfg.nav_links : DEFAULT_NAV;
  const tel = cfg.phone || phone;

  useEffect(() => {
    const t = getMemberToken();
    if (!t) return;
    api.get("/member/me", { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setMember(r.data)).catch(() => clearMember());
  }, []);

  const logout = () => { clearMember(); setMember(null); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70">
      <div className="container-cz flex items-center justify-between h-[72px]">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {nav.map((n) => (
            <NavLinkItem
              key={n.label}
              item={n}
              className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
            />
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          <a href={`tel:${tel}`} data-testid="call-us" className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-primary transition-colors">
            <Phone className="w-4 h-4" /> {tel}
          </a>
          {member ? (
            <div className="flex items-center gap-3">
              <Link to="/member" data-testid="member-account" className="flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-primary">
                <span className="w-9 h-9 rounded-full bg-sky-50 text-primary flex items-center justify-center"><User className="w-4 h-4" /></span>
                {(member.name || "Account").split(" ")[0]}
              </Link>
              <button onClick={logout} data-testid="member-logout" className="text-slate-400 hover:text-rose-600" aria-label="Sign out"><LogOut className="w-4 h-4" /></button>
            </div>
          ) : (
            <Link to="/account" data-testid="account-entry"
              className="flex items-center gap-2 rounded-full h-10 px-5 bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
              <User className="w-4 h-4" /> Sign In
            </Link>
          )}
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden p-2 text-slate-700" data-testid="mobile-menu-btn" aria-label="Open menu">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px]">
            <div className="flex items-center justify-between mb-8">
              <Logo />
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-col gap-1">
              {nav.map((n) => (
                <NavLinkItem key={n.label} item={n} onClick={() => setOpen(false)}
                  className="py-3 px-2 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-100" />
              ))}
              {member ? (
                <>
                  <Link to="/member" onClick={() => setOpen(false)}
                    className="flex items-center justify-center gap-2 rounded-full h-11 mt-4 bg-primary text-white font-semibold"><User className="w-4 h-4" /> My Account</Link>
                  <button onClick={() => { setOpen(false); logout(); }} className="flex items-center justify-center gap-2 mt-2 text-sm font-medium text-rose-600"><LogOut className="w-4 h-4" /> Sign out</button>
                </>
              ) : (
                <Link to="/account" onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-full h-11 mt-4 bg-primary text-white font-semibold"><User className="w-4 h-4" /> Sign In</Link>
              )}
              <a href={`tel:${tel}`} className="flex items-center justify-center gap-2 mt-3 text-sm font-medium text-slate-600">
                <Phone className="w-4 h-4" /> {tel}
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
